import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AlarmClock, Bell, BellRing, CircleAlert, Plug, UserCheck, Zap, type LucideIcon } from 'lucide-react';
import type { NotificationDto, NotificationListDto, NotificationType } from '@onesource/shared';
import { api } from '@/lib/api';
import { cn, relativeTime } from '@/lib/utils';
import { useWorkspace } from '@/hooks/session';
import { useSmartOperations } from '@/hooks/features';
import { Button } from './ui/button';
import { Popover, PopoverContent, PopoverTrigger, Tooltip } from './ui/overlays';

const ICONS: Record<NotificationType, LucideIcon> = {
  inquiry_assigned: UserCheck,
  inquiry_reassigned: UserCheck,
  follow_up_due: AlarmClock,
  follow_up_overdue: AlarmClock,
  response_overdue: CircleAlert,
  response_escalated: BellRing,
  automation_failed: Zap,
  channel_attention: Plug,
  rule_notification: Bell,
};

export function useNotifications() {
  const { workspace } = useWorkspace();
  const enabled = useSmartOperations();
  return useQuery({
    queryKey: ['ws', workspace.id, 'notifications'],
    queryFn: () => api.get<NotificationListDto>(`/api/workspaces/${workspace.id}/notifications?limit=20`),
    enabled,
    refetchInterval: 60_000,
  });
}

function NotificationRow({ n, onOpen }: { n: NotificationDto; onOpen: (n: NotificationDto) => void }) {
  const Icon = ICONS[n.type] ?? Bell;
  return (
    <li>
      <button
        onClick={() => onOpen(n)}
        className={cn('flex w-full items-start gap-2.5 rounded-md px-2 py-2 text-left hover:bg-hover', !n.read && 'bg-accent-soft/40')}
      >
        <Icon className={cn('mt-0.5 size-4 shrink-0', n.type.includes('overdue') || n.type === 'response_escalated' || n.type === 'automation_failed' ? 'text-warning' : 'text-muted')} aria-hidden />
        <span className="min-w-0 flex-1">
          <span className={cn('block text-[13px]', !n.read && 'font-medium')}>{n.title}</span>
          {n.body && <span className="block truncate text-xs text-muted">{n.body}</span>}
          <span className="block text-[11px] text-subtle">{relativeTime(n.createdAt)}</span>
        </span>
        {!n.read && <span className="mt-1.5 size-2 shrink-0 rounded-full bg-accent" aria-label="Unread" />}
      </button>
    </li>
  );
}

/** Bell with unread count; the list opens in a popover. Hidden unless smart operations are on. */
export function NotificationsBell({ collapsed, variant = 'nav' }: { collapsed?: boolean; variant?: 'nav' | 'icon' }) {
  const { workspace, base } = useWorkspace();
  const enabled = useSmartOperations();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const list = useNotifications();
  const key = ['ws', workspace.id, 'notifications'];
  const markRead = useMutation({
    mutationFn: (body: { ids?: string[]; all?: boolean }) => api.post(`/api/workspaces/${workspace.id}/notifications/read`, body),
    onSuccess: () => void qc.invalidateQueries({ queryKey: key }),
  });
  if (!enabled) return null;
  const unread = list.data?.unread ?? 0;
  const label = unread ? `Notifications (${unread} unread)` : 'Notifications';
  const openItem = (n: NotificationDto) => {
    if (!n.read) markRead.mutate({ ids: [n.id] });
    setOpen(false);
    if (n.link) navigate(`${base}/${n.link}`);
  };

  const trigger =
    variant === 'icon' ? (
      <Button variant="ghost" size="icon" aria-label={label} className="relative">
        <Bell />
        {unread > 0 && <span className="absolute right-1.5 top-1.5 size-2 rounded-full bg-accent" />}
      </Button>
    ) : (
      <button
        aria-label={label}
        className={cn('relative flex h-8 w-full items-center gap-2.5 rounded-md px-2 text-[13px] font-medium text-muted transition-colors hover:bg-hover hover:text-foreground', collapsed && 'justify-center px-0', open && 'bg-hover text-foreground')}
      >
        <Bell className="size-4 shrink-0" />
        {!collapsed && <span className="flex-1 truncate text-left">Notifications</span>}
        {!collapsed && unread > 0 && <span className="rounded bg-accent-soft px-1.5 text-[11px] font-semibold leading-5 text-accent">{unread > 99 ? '99+' : unread}</span>}
        {collapsed && unread > 0 && <span className="absolute ml-5 -mt-4 size-1.5 rounded-full bg-accent" />}
      </button>
    );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      {collapsed ? (
        <Tooltip content="Notifications" side="right">
          <PopoverTrigger asChild>{trigger}</PopoverTrigger>
        </Tooltip>
      ) : (
        <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      )}
      <PopoverContent side={variant === 'icon' ? 'bottom' : 'right'} align={variant === 'icon' ? 'end' : 'end'} className="w-[min(22rem,calc(100vw-2rem))] p-2">
        <div className="flex items-center justify-between px-2 pb-1.5">
          <p className="text-[13px] font-semibold">Notifications</p>
          {unread > 0 && (
            <button className="text-xs text-accent hover:underline" onClick={() => markRead.mutate({ all: true })}>
              Mark all read
            </button>
          )}
        </div>
        {list.isLoading ? (
          <p className="px-2 py-6 text-center text-xs text-muted">Loading…</p>
        ) : !list.data?.items.length ? (
          <p className="px-2 py-6 text-center text-xs text-muted">Nothing yet. Assignments, follow-up reminders and response-time alerts appear here.</p>
        ) : (
          <ul className="max-h-96 space-y-0.5 overflow-y-auto scrollbar-thin">
            {list.data.items.map((n) => (
              <NotificationRow key={n.id} n={n} onOpen={openItem} />
            ))}
          </ul>
        )}
      </PopoverContent>
    </Popover>
  );
}
